import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sun, Moon } from '@phosphor-icons/react';
import { TFMark } from './TFLogo';
import usePublicTheme from '../hooks/usePublicTheme';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { id: 'Gallery', label: 'Gallery', desc: 'Galleries & dealers' },
  { id: 'Artist', label: 'Artist', desc: 'Studios & individual artists' },
  { id: 'Collector', label: 'Collector', desc: 'Private collectors' },
];

export default function WebSignupPage() {
  const [mode, toggleTheme] = usePublicTheme();
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // form | otp
  const [form, setForm] = useState({ name: '', email: '', org: '', country: '', role: 'Gallery' });
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [resendIn, setResendIn] = useState(0);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Resend cooldown countdown
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const startSignup = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!form.name.trim() || !form.email.includes('@')) {
      setError('Please enter a valid name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/signup/web/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Sign-up failed');
      if (data.status === 'already_verified') {
        // Skip OTP entirely
        const sess = data.kyc_session ? `&session=${encodeURIComponent(data.kyc_session)}` : '';
        navigate(`/kyc/${encodeURIComponent(data.external_user_id)}?tfid=${encodeURIComponent(data.trustframe_id || '')}${sess}`);
        return;
      }
      if (data.dev_code) setInfo(`Dev mode: code is ${data.dev_code}`);
      setResendIn(30);
      setStep('otp');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setError(null);
    if (code.trim().length !== 6) {
      setError('Please enter the 6-digit code from your email.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/signup/web/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim().toLowerCase(), code: code.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Verification failed');
      const sess = data.kyc_session ? `&session=${encodeURIComponent(data.kyc_session)}` : '';
      navigate(`/kyc/${encodeURIComponent(data.external_user_id)}?tfid=${encodeURIComponent(data.trustframe_id || '')}${sess}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    setError(null);
    setInfo(null);
    try {
      const r = await fetch(`${API}/api/signup/web/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim().toLowerCase() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Resend failed');
      setInfo('A new code was sent.');
      setResendIn(30);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-tf-bg text-tf-text" data-testid="signup-page">
      <header className="border-b border-tf-border-muted px-4 sm:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4 sm:gap-6 min-h-[44px]" data-testid="signup-home-link">
            <TFMark color="currentColor" size={26} />
            <span className="font-machina font-bold text-[11px] sm:text-xs uppercase" style={{ letterSpacing: '0.95em', paddingLeft: '0.95em' }}>TRUSTFRAME</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={toggleTheme}
              data-testid="signup-theme-toggle"
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="min-h-[36px] min-w-[36px] px-2 inline-flex items-center justify-center border border-tf-border-muted text-tf-text-2 hover:text-tf-text transition-colors"
            >
              {mode === 'dark' ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
            </button>
            <Link to="/" className="font-mono text-[10px] tracking-[0.2em] uppercase text-tf-text-2 hover:text-tf-text flex items-center gap-2">
              <ArrowLeft size={12} weight="bold" /> Back
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-8 py-8 sm:py-12 md:py-16">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-tf-primary mb-3" data-testid="signup-step-label">
          [ .{step === 'form' ? 'CREATE' : 'VERIFY'}. .{step === 'form' ? 'ACCOUNT' : 'EMAIL'}. ]
        </p>
        <h1 className="font-heading font-bold text-2xl sm:text-4xl md:text-5xl tracking-tight mb-4 leading-tight">
          {step === 'form' ? 'Sign up for TrustFrame' : 'Check your inbox'}
        </h1>
        <p className="font-mono text-xs sm:text-sm text-tf-text-2 mb-8 sm:mb-10 leading-relaxed break-words">
          {step === 'form'
            ? 'Three quick steps: details, email verification, identity check. Most users finish in under 5 minutes.'
            : <>We sent a 6-digit code to <span className="text-tf-text break-all">{form.email}</span>. Enter it below to continue.</>}
        </p>

        {step === 'form' && (
          <form onSubmit={startSignup} className="space-y-5 sm:space-y-6" data-testid="signup-form">
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-tf-text-2 block mb-3">Role</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {ROLES.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setForm({ ...form, role: r.id })}
                    data-testid={`signup-role-${r.id.toLowerCase()}`}
                    className={`min-h-[64px] p-4 border text-left transition-all ${
                      form.role === r.id ? 'border-tf-primary bg-tf-primary text-white' : 'border-tf-border-light text-tf-text hover:border-tf-primary active:border-tf-primary'
                    }`}
                  >
                    <p className="font-heading font-bold text-sm tracking-wide">{r.label}</p>
                    <p className={`font-mono text-[9px] mt-1 ${form.role === r.id ? 'text-white/80' : 'text-tf-text-2'}`}>{r.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <Field label="Full name" name="name" value={form.name} onChange={update('name')} testid="signup-name" required />
            <Field label="Email" name="email" type="email" value={form.email} onChange={update('email')} testid="signup-email" required />
            <Field label="Organisation (optional)" name="org" value={form.org} onChange={update('org')} testid="signup-org" />
            <Field label="Country" name="country" value={form.country} onChange={update('country')} testid="signup-country" placeholder="e.g. United Kingdom" />

            {error && <p className="font-mono text-xs text-tf-danger break-words" data-testid="signup-error">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              data-testid="signup-submit-btn"
              className="w-full sm:w-auto min-h-[48px] px-6 py-3 bg-tf-primary text-white font-mono text-[10px] tracking-[0.25em] uppercase hover:bg-tf-accent transition-all disabled:opacity-50 inline-flex items-center justify-center gap-3"
            >
              {submitting ? 'Sending code…' : 'Send verification code'}
              {!submitting && <ArrowRight size={14} weight="bold" />}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={verifyCode} className="space-y-5 sm:space-y-6" data-testid="otp-form">
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-tf-text-2 block mb-2">6-digit code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                data-testid="otp-input"
                placeholder="000000"
                className="w-full px-4 py-4 bg-tf-surface border border-tf-border-light focus:border-tf-primary outline-none font-mono text-xl sm:text-2xl tracking-[0.3em] sm:tracking-[0.4em] text-center transition-colors"
                autoFocus
              />
            </div>

            {info && <p className="font-mono text-xs text-tf-text-2 break-words" data-testid="signup-info">{info}</p>}
            {error && <p className="font-mono text-xs text-tf-danger break-words" data-testid="otp-error">{error}</p>}

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <button
                type="submit"
                disabled={submitting || code.length !== 6}
                data-testid="otp-verify-btn"
                className="w-full sm:w-auto min-h-[48px] px-6 py-3 bg-tf-primary text-white font-mono text-[10px] tracking-[0.25em] uppercase hover:bg-tf-accent transition-all disabled:opacity-50 inline-flex items-center justify-center gap-3"
              >
                {submitting ? 'Verifying…' : 'Verify & Continue'}
                {!submitting && <ArrowRight size={14} weight="bold" />}
              </button>

              <button
                type="button"
                onClick={resend}
                disabled={resendIn > 0}
                data-testid="otp-resend-btn"
                className="min-h-[40px] px-2 font-mono text-[10px] tracking-[0.2em] uppercase text-tf-text-2 hover:text-tf-primary disabled:opacity-50"
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
            </div>

            <button
              type="button"
              onClick={() => { setStep('form'); setError(null); setInfo(null); setCode(''); }}
              data-testid="otp-back-btn"
              className="min-h-[40px] font-mono text-[10px] tracking-[0.2em] uppercase text-tf-text-2 hover:text-tf-primary inline-flex items-center gap-2"
            >
              <ArrowLeft size={12} weight="bold" /> Use a different email
            </button>
          </form>
        )}

        <p className="mt-10 font-mono text-[10px] tracking-[0.15em] uppercase text-tf-text-2 break-words">
          Already on Telegram?{' '}
          <a href="https://t.me/TrustframeBot" className="text-tf-primary hover:underline" data-testid="signup-telegram-link">Use the bot →</a>
        </p>
      </main>
    </div>
  );
}

function Field({ label, name, value, onChange, type = 'text', testid, required, placeholder }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-tf-text-2 block mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        autoComplete={type === 'email' ? 'email' : 'off'}
        className="w-full min-h-[48px] px-4 py-3 bg-tf-surface border border-tf-border-light focus:border-tf-primary outline-none font-mono text-sm transition-colors"
      />
    </div>
  );
}
